export type ErrorContext = {
  module: string;
  operation: string;
  details?: Record<string, unknown>;
};

// Utility functions that can be used in non-React contexts
// NOTA: getErrorMessage devuelve el mensaje CRUDO (puede venir en inglés desde
// Supabase/Postgres/GoTrue). Úsalo solo para logging; para mostrar al usuario
// usa SIEMPRE translateSupabaseError(...).message.
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message);
  }
  return 'Error desconocido';
};

// ---------------------------------------------------------------------------
// Traducción central de errores a mensajes aptos para el usuario (español).
// ---------------------------------------------------------------------------

/** Mensajes por código de PostgREST/Postgres. */
const PG_CODE_MESSAGES: Record<string, string> = {
  '23505': 'Ya existe un registro con estos datos.',
  '23503': 'No se puede completar: hay datos relacionados que dependen de este registro.',
  '23502': 'Falta un dato obligatorio.',
  '22P02': 'Formato de dato inválido.',
  '42501': 'No tienes permisos para realizar esta acción.',
  '57014': 'La consulta tardó demasiado. Intenta con un rango de tiempo menor.',
  PGRST116: 'El registro solicitado no existe.',
  PGRST301: 'Tu sesión expiró. Vuelve a iniciar sesión.',
};

/** Mensajes por nombre de constraint CHECK (código 23514). */
const CHECK_CONSTRAINT_MESSAGES: Record<string, string> = {
  threshold_rules_window_check:
    'La ventana de tiempo no es válida para este tipo de regla.',
  threshold_rules_target_check:
    'La regla necesita un sensor (o un dispositivo, si es de "sin datos").',
  threshold_rules_cooldown_check: 'El reposo entre alertas no puede ser negativo.',
  threshold_rules_clear_threshold_check:
    'El umbral de cierre debe quedar del lado seguro: por debajo del de apertura en reglas de "mayor que", y por encima en las de "menor que".',
  threshold_rules_clear_after_check:
    'Las lecturas para cerrar la alerta deben estar entre 1 y 20.',
  threshold_rules_source_coherent_check:
    'A la regla le faltan datos de su origen: pronóstico necesita estación, variable y horizonte (0–6 días); acumulado histórico necesita sensor, métrica y ventana.',
  profiles_quiet_hours_check:
    'El horario silencioso necesita hora de inicio y de fin, y deben ser distintas.',
  calibration_interval_check:
    'El intervalo de calibración solo aplica si el sensor requiere calibración.',
};

/** Mensajes por código de error de Supabase Auth (GoTrue v2, error.code). */
const AUTH_CODE_MESSAGES: Record<string, string> = {
  invalid_credentials: 'Correo o contraseña incorrectos.',
  email_not_confirmed: 'Tu correo aún no está confirmado. Revisa tu bandeja de entrada.',
  over_email_send_rate_limit:
    'Se han enviado demasiados correos. Espera unos minutos e intenta de nuevo.',
  over_request_rate_limit:
    'Demasiados intentos. Espera unos minutos e intenta de nuevo.',
  same_password: 'La nueva contraseña debe ser diferente a la actual.',
  weak_password: 'La contraseña es demasiado débil. Usa al menos 8 caracteres.',
  email_exists: 'Ya existe una cuenta con este correo.',
  user_not_found: 'No existe una cuenta con este correo.',
  otp_expired: 'El enlace expiró o ya fue usado. Solicita uno nuevo.',
  session_not_found: 'Tu sesión expiró. Vuelve a iniciar sesión.',
  session_expired: 'Tu sesión expiró. Vuelve a iniciar sesión.',
  validation_failed: 'Los datos proporcionados no son válidos.',
  user_banned: 'Esta cuenta está deshabilitada. Contacta al administrador.',
};

/** Fallbacks por patrón de texto cuando no hay código utilizable. */
const PATTERN_MESSAGES: Array<[RegExp, string]> = [
  [/failed to fetch|networkerror|network request failed|load failed/i,
    'Sin conexión. Revisa tu internet e intenta de nuevo.'],
  [/rate limit|you can only request this after/i,
    'Demasiados intentos. Espera unos minutos e intenta de nuevo.'],
  [/jwt expired|invalid.*jwt|refresh.?token/i,
    'Tu sesión expiró. Vuelve a iniciar sesión.'],
  [/row-level security/i, 'No tienes permisos para realizar esta acción.'],
  [/edge function returned a non-2xx/i,
    'El servidor no pudo completar la operación. Intenta de nuevo.'],
  [/failed to send a request to the edge function/i,
    'Sin conexión con el servidor. Revisa tu internet e intenta de nuevo.'],
  [/invalid login credentials/i, 'Correo o contraseña incorrectos.'],
  [/email not confirmed/i,
    'Tu correo aún no está confirmado. Revisa tu bandeja de entrada.'],
  [/password should be at least/i,
    'La contraseña es demasiado corta. Usa al menos 8 caracteres.'],
  [/new password should be different/i,
    'La nueva contraseña debe ser diferente a la actual.'],
  [/auth session missing/i, 'Tu sesión expiró. Vuelve a iniciar sesión.'],
  [/timeout|timed out/i, 'La operación tardó demasiado. Intenta de nuevo.'],
];

/** ¿El texto parece ya escrito en español (mensajes propios o RAISE de los RPCs)? */
function looksSpanish(text: string): boolean {
  return /[áéíóúñ¿¡]|\b(el|la|los|las|de|no|una?|para|del|est[aá]|sesi[oó]n|correo|datos)\b/i.test(
    text
  );
}

/**
 * Traduce cualquier error (PostgrestError, AuthError, Error, FunctionsError,
 * string…) a un mensaje apto para mostrar al usuario, SIEMPRE en español.
 *
 * Orden de resolución: código de auth → código Postgres/PostgREST (con submapa
 * de constraints CHECK) → passthrough de mensajes ya en español (RAISE
 * EXCEPTION de los RPCs, mensajes propios de los servicios) → patrones de
 * texto conocidos → fallback genérico.
 */
export function translateSupabaseError(
  error: unknown,
  fallback = 'Ocurrió un error inesperado. Intenta de nuevo.'
): { message: string; code?: string } {
  const rawMessage = getErrorMessage(error);
  const code = getErrorCode(error);

  if (code) {
    if (AUTH_CODE_MESSAGES[code]) {
      return { message: AUTH_CODE_MESSAGES[code], code };
    }
    if (code === '23514') {
      const named = Object.keys(CHECK_CONSTRAINT_MESSAGES).find((name) =>
        rawMessage.includes(name)
      );
      return {
        message: named
          ? CHECK_CONSTRAINT_MESSAGES[named]
          : 'Los datos no cumplen una regla de validación.',
        code,
      };
    }
    // P0001 = RAISE EXCEPTION de nuestras funciones SQL: sus mensajes ya están
    // escritos en español para el usuario, se dejan pasar tal cual.
    if (code === 'P0001' && rawMessage) {
      return { message: rawMessage, code };
    }
    if (PG_CODE_MESSAGES[code]) {
      return { message: PG_CODE_MESSAGES[code], code };
    }
  }

  for (const [pattern, message] of PATTERN_MESSAGES) {
    if (pattern.test(rawMessage)) {
      return { message, code };
    }
  }

  // Mensajes ya en español (servicios propios, RPCs sin código visible).
  if (rawMessage && rawMessage !== 'Error desconocido' && looksSpanish(rawMessage)) {
    return { message: rawMessage, code };
  }

  return { message: fallback, code };
}

// Extrae el código original de un error desconocido (p.ej. el code de un PostgrestError),
// para conservar el detalle del constraint sin exponer el mensaje crudo al usuario.
export const getErrorCode = (error: unknown): string | undefined => {
  if (error && typeof error === 'object' && 'code' in error && error.code != null) {
    // supabase-js entrega code '' en fallos de red: eso no es un código.
    return String((error as { code: unknown }).code) || undefined;
  }
  return undefined;
};

export const logErrorToConsole = (error: unknown, context: ErrorContext): void => {
  const errorMessage = getErrorMessage(error);
  const timestamp = new Date().toISOString();
  
  if (import.meta.env.DEV) {
    console.error(`[${timestamp}] ${context.module}::${context.operation}`, {
      error,
      message: errorMessage,
      details: context.details
    });
  }
};